import ToolbarButtonPopover from "../toolbar/ToolbarButtonPopover";
import React, { useState, useEffect } from "react";
import { Box, Typography, Slider, Checkbox } from "@mui/material";
import { useTreatments } from "../../api/treatments";
import { useUnitSystem } from "../../hooks/useUnitSystem";
import { formatFuelLoad } from "../../utils/units";
import { Filter } from "../layerControl/FilterModal";
import { applyTreatmentFilters } from "../../utils/treatmentFilters";
import { treatmentYearColor } from "../../utils/treatmentDisplay";

// Feature colour keyed to the EndDate year; shared with the scenario bar so
// each row's swatch matches its polygon.
const getColorForDate = treatmentYearColor;

// Type definitions
type RangeValues = {
  min: number;
  max: number;
};

// Dynamic Range Slider component (copied from FuelLoadLayer for consistency)
const DynamicRangeSlider: React.FC<{
  label: string;
  values: RangeValues;
  min: number;
  max: number;
  step: number;
  onChange: (values: RangeValues) => void;
}> = ({ label, values, min, max, step, onChange }) => {
  const [sliderValues, setSliderValues] = useState<number[]>([values.min, values.max]);

  const handleChange = (_: any, newValues: number | number[]) => {
    if (Array.isArray(newValues)) {
      setSliderValues(newValues);
    }
  };

  const handleChangeCommitted = (_: any, newValues: number | number[]) => {
    if (Array.isArray(newValues)) {
      onChange({ min: newValues[0], max: newValues[1] });
    }
  };

  return (
    <Box sx={{ p: "0 16px 10px 16px", maxWidth: 250 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="caption">{label} Range:</Typography>
        <Typography variant="caption" fontWeight="bold">
          {sliderValues[0]} - {sliderValues[1]}
        </Typography>
      </Box>
      <Slider
        value={sliderValues}
        min={min}
        max={max}
        step={step}
        onChange={handleChange}
        onChangeCommitted={handleChangeCommitted}
        valueLabelDisplay="off"
        size="small"
      />
    </Box>
  );
};

const TreatmentsLayer = (id: string, name: string, filters: Filter[] = []) => {
  const { treatments } = useTreatments();
  const unitSystem = useUnitSystem();

  // Raw and filtered geojson
  const [geojson, setGeojson] = useState<any>(null);
  const [filteredGeojson, setFilteredGeojson] = useState<any>(null);

  // Visibility and stale flag
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [stale, setStale] = useState(false);

  // Year range state (updated once data is available)
  const [dataYearBounds, setDataYearBounds] = useState<{ min: number; max: number } | null>(null);
  const [yearRange, setYearRange] = useState<RangeValues>({ min: 0, max: 0 });

  // Build raw geojson once API data arrives
  useEffect(() => {
    if (!treatments || !treatments.data) return;

    // Handle empty feature collection
    if (treatments.data.features.length === 0) {
      const emptyGeo = {
        type: "FeatureCollection",
        features: [],
      };
      setGeojson(emptyGeo);
      setFilteredGeojson(emptyGeo);
      setDataYearBounds(null);
      setYearRange({ min: 0, max: 0 });
      setStale(true);
      return;
    }

    // Determine min / max year across all provided dates
    const allYears: number[] = [];
    treatments.data.features.forEach((f: any) => {
      [f.properties?.StartDate, f.properties?.EndDate, f.properties?.VegDate].forEach((d: string | undefined) => {
        if (d) {
          const y = new Date(d).getFullYear();
          if (!isNaN(y)) allYears.push(y);
        }
      });
    });

    if (allYears.length === 0) return; // unexpected but guard

    const minYear = Math.min(...allYears);
    const maxYear = Math.max(...allYears);

    setDataYearBounds({ min: minYear, max: maxYear });
    setYearRange({ min: minYear, max: maxYear });

    const newGeo = {
      type: "FeatureCollection",
      features: treatments.data.features.map((feature: any) => {
        const color = getColorForDate(feature.properties?.EndDate);
        const rawFuel = feature.properties?.ResidualFuel;
        const fuelValue = typeof rawFuel === "number" ? rawFuel : Number(rawFuel);
        const fuelLabel = Number.isFinite(fuelValue)
          ? formatFuelLoad(fuelValue, unitSystem)
          : "";
        return {
          ...feature,
          properties: {
            ...feature.properties,
            color,
            hoverLabel: `${feature.properties?.Name || "Treatment"} -- ${feature.properties?.EndDate || ""} -- ${feature.properties?.Type || ""} -- ${fuelLabel}`,
          },
        };
      }),
    };

    setGeojson(newGeo);
    setFilteredGeojson(newGeo); // initial (full) view but will be hidden until visible is toggled
    setStale(true);
  }, [treatments, unitSystem]);

  // Helper to check feature visibility against current yearRange
  const featureInRange = (feature: any, range: RangeValues): boolean => {
    const sd = feature.properties?.StartDate ? new Date(feature.properties.StartDate) : null;
    const ed = feature.properties?.EndDate ? new Date(feature.properties.EndDate) : null;
    const vd = feature.properties?.VegDate ? new Date(feature.properties.VegDate) : null;

    const fStart = range.min;
    const fEnd = range.max;

    if (sd && ed) {
      const sY = sd.getFullYear();
      const eY = ed.getFullYear();
      if (eY < fStart || sY > fEnd) return false;
      return true;
    }

    if (ed) {
      const y = ed.getFullYear();
      return y >= fStart && y <= fEnd;
    }

    if (vd) {
      const y = vd.getFullYear();
      return y <= fEnd;
    }

    return false; // If no dates, hide
  };

  // Refilter geojson whenever visibility, yearRange, or filters change
  useEffect(() => {
    if (!geojson) return;

    if (!isVisible) {
      setFilteredGeojson({ ...geojson, features: [] });
      setStale(true);
      return;
    }

    // First apply year range filter
    const yearFiltered = {
      ...geojson,
      features: geojson.features.filter((f: any) => featureInRange(f, yearRange)),
    };

    // Then apply user-defined filters
    const filtered = applyTreatmentFilters(yearFiltered, filters);

    setFilteredGeojson(filtered);
    setStale(true);
  }, [isVisible, yearRange, geojson, filters]);

  // Layer style (visible controlled via layout)
  const fillLayerStyle = {
    id: id,
    type: "fill",
    paint: {
      "fill-color": ["get", "color"],
      "fill-opacity": 0.5,
      "fill-outline-color": "#000",
    },
    layout: {
      visibility: isVisible ? "visible" : "none",
    },
  };

  // Label layer showing treatment name at polygon centroid
  const labelLayerStyle = {
    id: `${id}-labels`,
    type: "symbol",
    layout: {
      "text-field": ["get", "Name"],
      "text-size": 14,
      "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"],
      "text-anchor": "center",
      "text-allow-overlap": false,
      "text-ignore-placement": false,
      visibility: isVisible ? "visible" : "none",
    },
    paint: {
      "text-color": "#ffffff",
      "text-halo-color": "rgba(0, 0, 0, 0.8)",
      "text-halo-width": 1.5,
    },
  };

  // Custom footer with visibility checkbox and optional year slider
  const customFooter = (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", pl: 1, pr: 2 }}>
        <Checkbox
          size="small"
          checked={isVisible}
          onChange={(e) => setIsVisible(e.target.checked)}
        />
        <Typography variant="caption">Visible</Typography>
      </Box>
      {dataYearBounds && (
        <DynamicRangeSlider
          label="Year"
          values={yearRange}
          min={dataYearBounds.min}
          max={dataYearBounds.max}
          step={1}
          onChange={(vals) => setYearRange(vals)}
        />
      )}
    </Box>
  );

  const toolbarButton = (
    <ToolbarButtonPopover
      key={id}
      id={id}
      name={"Treatments"}
      enabled={!!geojson}
      toggledOn={isVisible}
      icon={"agriculture"}
      options={[]}
      onClick={() => setIsVisible(!isVisible)}
      stayOpenAfterSelection={true}
      customFooter={customFooter}
    />
  );

  return {
    id,
    name,
    source: filteredGeojson,
    rawSource: geojson,
    layerStyle: [fillLayerStyle, labelLayerStyle],
    toolbarButton,
    active: isVisible,
    stale,
    setStale,
  };
};

export default TreatmentsLayer;
